/**
 * Símbolos insertables del editor, DERIVADOS de un `VariableSchema` (puro).
 *
 * Reemplaza el viejo `buildSymbols` que importaba el dominio de negociaciones: ahora
 * todo se calcula a partir del esquema que el host provee por el puerto
 * `VariableSchemaProvider`. El engine no conoce el dominio.
 */

import { FORMULA_FUNCTIONS } from '@idc-forms/formula/formula-eval'
import {
  LOOP_INDEX_EXAMPLES,
  LOOP_INDEX_FIELDS,
  type LoopIndexField
} from '@/document-engine/domain/loop/index-fields'
import type {
  VariableEnumValue,
  VariableSchema,
  VariableType
} from '@/shared/variables/variable-schema'

export type SymbolKind = 'var' | 'field' | 'helper' | 'func'

export interface InsertableSymbol {
  token: string
  label: string
  kind: SymbolKind
  category: string
  group?: string // subgrupo dentro de la categoría (para anidar en el picker)
  type?: VariableType
  enumValues?: VariableEnumValue[]
  description?: string
  example?: string
}

const TYPE_LABEL: Record<VariableType, string> = {
  string: 'texto',
  number: 'número',
  date: 'fecha',
  boolean: 'sí/no',
  enum: 'opción'
}
const FUNCTION_CATEGORY = 'Funciones'

const cap = (s: string): string => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s)

// ── Construcción de símbolos ──────────────────────────────────────────────────

/** Todos los símbolos insertables (variables + campos de loop + helpers + funciones). */
export const buildInsertableSymbols = (schema: VariableSchema): InsertableSymbol[] => {
  const out: InsertableSymbol[] = []
  const preview = schema.previewValues ?? {}

  // 1. Escalares (por grupo de categoría).
  for (const group of schema.scalars) {
    for (const f of group.fields) {
      out.push({
        token: f.token,
        label: f.label,
        kind: 'var',
        category: group.label,
        group: f.group,
        type: f.type,
        enumValues: f.enumValues,
        description:
          f.description ?? `Dato del documento${f.type ? ` (${TYPE_LABEL[f.type]})` : ''}.`,
        example: f.example ?? preview[f.token]
      })
    }
  }

  // 2. Colecciones (+ anidadas de 1 nivel) con campos `alias.campo` + helpers de índice.
  // Cada colección es su propia CATEGORÍA (su label), para que el menú/sidebar agrupe y
  // ordene por scope (ej. "Clientes (comparecientes)", "Cuotas").
  const addCollection = (
    alias: string,
    categoryLabel: string,
    fields: {
      token: string
      label: string
      type?: VariableType
      enumValues?: VariableEnumValue[]
    }[]
  ): void => {
    for (const f of fields) {
      const token = `${alias}.${f.token}`
      out.push({
        token,
        label: `${cap(alias)} · ${f.label}`,
        kind: 'field',
        category: categoryLabel,
        type: f.type ?? 'string',
        enumValues: f.enumValues,
        description: `Campo del item dentro del loop “${alias}”.`,
        example: preview[token]
      })
    }
    for (const h of LOOP_INDEX_FIELDS as LoopIndexField[]) {
      out.push({
        token: `${alias}.${h.key}`,
        label: `${cap(alias)} · ${h.label}`,
        kind: 'helper',
        category: categoryLabel,
        description: 'Dato de posición del item dentro del loop (lo calcula el motor).',
        example: LOOP_INDEX_EXAMPLES[h.key]
      })
    }
  }
  for (const c of schema.collections) {
    addCollection(c.alias, c.label, c.fields)
    // Colecciones anidadas (1 nivel): expuestas como `alias.subalias.campo`.
    for (const child of c.children ?? [])
      addCollection(`${c.alias}.${child.alias}`, child.label, child.fields)
  }

  // 3. Funciones de fórmula.
  for (const fn of FORMULA_FUNCTIONS) {
    out.push({
      token: `${fn.name.split('(')[0]}(`,
      label: fn.name,
      kind: 'func',
      category: FUNCTION_CATEGORY,
      description: fn.desc,
      example: fn.example
    })
  }

  return out
}

// ── Oráculos derivados ────────────────────────────────────────────────────────

/** Mapa token → label de símbolos que NO son funciones (label curado del chip). */
export const buildLabelByToken = (symbols: InsertableSymbol[]): Map<string, string> =>
  new Map(symbols.filter((s) => s.kind !== 'func').map((s) => [s.token, s.label]))

/** Mapa token → tipo declarado (para parsear fechas/números al evaluar fórmulas). */
export const buildTypeByToken = (symbols: InsertableSymbol[]): Map<string, VariableType> => {
  const out = new Map<string, VariableType>()
  for (const s of symbols) if (s.kind !== 'func' && s.type) out.set(s.token, s.type)
  return out
}

/**
 * Conjunto AMPLIO de tokens conocidos para el RESALTADOR de fórmulas: tokens completos
 * + nombres de colección + claves "peladas" de campo + `coleccion.campo`. (Más laxo
 * que el oráculo de validez del chip, a propósito: evita marcar en rojo argumentos
 * sueltos de `unir(...)`.)
 */
export const buildKnownTokens = (
  schema: VariableSchema,
  symbols: InsertableSymbol[]
): Set<string> => {
  const out = new Set<string>()
  for (const s of symbols) if (s.kind !== 'func') out.add(s.token)
  for (const c of schema.collections) {
    out.add(c.name)
    for (const f of c.fields) {
      out.add(f.token) // clave pelada (ej. 'nombre')
      out.add(`${c.name}.${f.token}`) // coleccion.campo
    }
  }
  for (const t of schema.legacyTokens ?? []) out.add(t)
  return out
}

/** Valores de muestra para la previsualización en vivo (escalares + items + helpers). */
export const buildPreviewValues = (schema: VariableSchema): Record<string, string> => {
  const out: Record<string, string> = { ...(schema.previewValues ?? {}) }
  for (const c of schema.collections) {
    for (const h of LOOP_INDEX_FIELDS) out[`${c.alias}.${h.key}`] = LOOP_INDEX_EXAMPLES[h.key]
  }
  return out
}

/** ¿Es `token` una variable/campo conocido? Oráculo de validez del chip del editor. */
export const isTokenKnown = (
  labelByToken: Map<string, string>,
  legacyTokens: string[],
  token: string
): boolean => labelByToken.has(token) || legacyTokens.includes(token)

// Sufijos que el redactor escribe creyendo que son variables pero son MÁSCARAS.
const MASK_SUFFIXES: Array<{ suffix: string; hint: string }> = [
  { suffix: '_en_letras', hint: 'en letras' },
  { suffix: '_letras', hint: 'en letras' },
  { suffix: '_en_numeros', hint: 'en números' },
  { suffix: '_numeros', hint: 'en números' }
]

// Distancia de edición (Levenshtein) acotada — para sugerir el token válido más parecido.
const editDistance = (a: string, b: string): number => {
  const m = a.length
  const n = b.length
  if (m === 0) return n
  if (n === 0) return m
  let prev = Array.from({ length: n + 1 }, (_, i) => i)
  let curr = new Array<number>(n + 1)
  for (let i = 1; i <= m; i++) {
    curr[0] = i
    for (let j = 1; j <= n; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1
      curr[j] = Math.min(prev[j] + 1, curr[j - 1] + 1, prev[j - 1] + cost)
    }
    ;[prev, curr] = [curr, prev]
  }
  return prev[n]
}

export interface VariableSuggestion {
  token: string
  hint?: string
}

/**
 * Para un token INVÁLIDO, sugiere el token válido más probable: (1) sufijo de máscara
 * mal escrito (el base existe), o (2) el token conocido más cercano por distancia de
 * edición. Devuelve null si es válido o no hay sugerencia razonable.
 */
export const suggestToken = (
  labelByToken: Map<string, string>,
  legacyTokens: string[],
  token: string
): VariableSuggestion | null => {
  if (token === '' || isTokenKnown(labelByToken, legacyTokens, token)) return null

  for (const { suffix, hint } of MASK_SUFFIXES) {
    if (token.endsWith(suffix)) {
      const base = token.slice(0, -suffix.length)
      if (isTokenKnown(labelByToken, legacyTokens, base)) return { token: base, hint }
    }
  }

  let best: string | null = null
  let bestDistance = Infinity
  for (const known of labelByToken.keys()) {
    const d = editDistance(token, known)
    if (d < bestDistance) {
      bestDistance = d
      best = known
    }
  }
  const threshold = Math.max(2, Math.floor(token.length * 0.34))
  if (best !== null && bestDistance <= threshold) return { token: best }

  return null
}

/** Búsqueda para picker/typeahead: filtra por texto en token o label. */
export const searchInSymbols = (symbols: InsertableSymbol[], query: string): InsertableSymbol[] => {
  const q = query.trim().toLowerCase()
  if (!q) return symbols
  return symbols.filter(
    (s) => s.token.toLowerCase().includes(q) || s.label.toLowerCase().includes(q)
  )
}
